import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, ShoppingCart, User, Package, RefreshCw, DollarSign,
  ChevronDown, ChevronUp, Plus, X, Search, Edit2, Trash2, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const timeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
};

export default function AdminCarts() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [carts, setCarts] = useState([]);
  const [expandedCarts, setExpandedCarts] = useState({});
  const [summary, setSummary] = useState({
    total_carts: 0,
    total_items: 0,
    total_value: 0
  });
  
  // Add Custom Item modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    user_email: '',
    product_id: '',
    product_name: '',
    variant_id: '',
    price: '',
    type: 'regular',
    quantity: 1
  });
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [itemsAddedCount, setItemsAddedCount] = useState(0); // resets each time modal opens
  
  // Inline edit state: { [`${user_id}-${item_index}`]: { price, quantity } }
  const [editingItem, setEditingItem] = useState(null);
  const [editDraft, setEditDraft] = useState({ price: '', quantity: 1 });
  const [itemBusy, setItemBusy] = useState(null); // key of item being saved/removed

  // Checkout-on-behalf modal state
  const [checkoutModal, setCheckoutModal] = useState({ open: false, cart: null });
  const [checkoutForm, setCheckoutForm] = useState({
    shipping_name: '', shipping_address: '', shipping_apt_unit: '',
    shipping_city: '', shipping_state: '', shipping_zip: '',
    priority_shipping: false,
    overnight_shipping: false,
    promo_code: '', gift_card_code: '',
    manual_discount: '', notes: '',
  });
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const openCheckoutModal = async (cart) => {
    // Pull the user's saved address so we can pre-fill the form
    try {
      const res = await axios.get(`${API}/admin/users/${cart.user_id}/orders`, { headers: getAuthHeader() });
      const u = res.data?.user || {};
      setCheckoutForm({
        shipping_name: u.full_name || cart.user_name || '',
        shipping_address: u.address || '',
        shipping_apt_unit: u.apt_unit || '',
        shipping_city: u.city || '',
        shipping_state: u.state || '',
        shipping_zip: u.zip_code || '',
        priority_shipping: false,
        overnight_shipping: false,
        promo_code: '',
        gift_card_code: '',
        manual_discount: '',
        notes: '',
      });
    } catch (_e) {
      // fall back to whatever we have
      setCheckoutForm({
        shipping_name: cart.user_name || '',
        shipping_address: '', shipping_apt_unit: '',
        shipping_city: '', shipping_state: '', shipping_zip: '',
        priority_shipping: false, overnight_shipping: false, promo_code: '', gift_card_code: '',
        manual_discount: '', notes: '',
      });
    }
    setCheckoutModal({ open: true, cart });
  };

  const submitAdminCheckout = async () => {
    if (!checkoutModal.cart) return;
    if (!checkoutForm.shipping_address || !checkoutForm.shipping_city || !checkoutForm.shipping_state || !checkoutForm.shipping_zip) {
      toast.error('Shipping address is incomplete');
      return;
    }
    setCheckoutBusy(true);
    try {
      const body = {
        shipping_name: checkoutForm.shipping_name,
        shipping_address: checkoutForm.shipping_address,
        shipping_apt_unit: checkoutForm.shipping_apt_unit || null,
        shipping_city: checkoutForm.shipping_city,
        shipping_state: checkoutForm.shipping_state,
        shipping_zip: checkoutForm.shipping_zip,
        priority_shipping: !!checkoutForm.priority_shipping,
        overnight_shipping: !!checkoutForm.overnight_shipping,
        promo_code: checkoutForm.promo_code || null,
        gift_card_code: checkoutForm.gift_card_code || null,
        manual_discount: parseFloat(checkoutForm.manual_discount) || 0,
        notes: checkoutForm.notes || null,
      };
      const res = await axios.post(
        `${API}/admin/carts/${checkoutModal.cart.user_id}/checkout`,
        body,
        { headers: getAuthHeader() }
      );
      toast.success(`ORDER ${res.data?.id || ''} CREATED FOR ${checkoutModal.cart.user_email}`);
      setCheckoutModal({ open: false, cart: null });
      fetchCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkout for user');
    } finally {
      setCheckoutBusy(false);
    }
  };

  const fetchCarts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/carts`, {
        headers: getAuthHeader()
      });
      setCarts(response.data.carts || []);
      setSummary({
        total_carts: response.data.total_carts || 0,
        total_items: response.data.total_items || 0,
        total_value: response.data.total_value || 0
      });
    } catch (error) {
      console.error('Failed to fetch carts:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  const toggleCart = (userId) => {
    setExpandedCarts(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Debounced product search
  useEffect(() => {
    if (!showAddModal || !productSearch || productSearch.length < 2) {
      setProductResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await axios.get(`${API}/products`, {
          params: { search: productSearch, include_offline: true, limit: 15 },
          headers: getAuthHeader()
        });
        setProductResults(res.data || []);
      } catch (e) {
        console.error('Product search failed', e);
        setProductResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [productSearch, showAddModal, getAuthHeader]);

  // Debounced user search
  useEffect(() => {
    if (!showAddModal || !userSearch || userSearch.length < 2) {
      setUserResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setSearchingUsers(true);
        const res = await axios.get(`${API}/admin/users/search`, {
          params: { q: userSearch, limit: 15 },
          headers: getAuthHeader()
        });
        setUserResults(res.data?.users || []);
      } catch (e) {
        console.error('User search failed', e);
        setUserResults([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [userSearch, showAddModal, getAuthHeader]);

  const openAddModal = () => {
    setAddForm({
      user_email: '',
      product_id: '',
      product_name: '',
      variant_id: '',
      price: '',
      type: 'regular',
      quantity: 1
    });
    setProductSearch('');
    setProductResults([]);
    setSelectedProduct(null);
    setUserSearch('');
    setUserResults([]);
    setSelectedUser(null);
    setItemsAddedCount(0);
    setShowAddModal(true);
  };

  // Reset product-side fields but keep the selected user so admin can rapid-add
  // multiple items to the same cart without re-searching.
  const resetProductFieldsOnly = () => {
    setAddForm(f => ({
      ...f,
      product_id: '',
      product_name: '',
      variant_id: '',
      price: '',
      type: 'regular',
      quantity: 1,
    }));
    setProductSearch('');
    setProductResults([]);
    setSelectedProduct(null);
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    setAddForm(f => ({ ...f, user_email: u.email }));
    setUserSearch(u.full_name ? `${u.full_name} (${u.email})` : u.email);
    setUserResults([]);
  };

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setAddForm(f => ({ ...f, product_id: p.id, product_name: p.name, variant_id: '' }));
    setProductSearch(p.name);
    setProductResults([]);
  };

  const submitAddItem = async () => {
    if (!addForm.user_email.trim()) {
      toast.error('Select a user');
      return;
    }
    if (!addForm.product_id) {
      toast.error('Select a product');
      return;
    }
    const priceNum = parseFloat(addForm.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Enter a valid price');
      return;
    }
    const qtyNum = parseInt(addForm.quantity, 10) || 1;
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API}/admin/carts/add-item`,
        {
          user_email: addForm.user_email.trim(),
          product_id: addForm.product_id,
          variant_id: addForm.variant_id || null,
          price: priceNum,
          type: addForm.type,
          quantity: qtyNum
        },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'Item added to cart');
      setItemsAddedCount(n => n + 1);
      resetProductFieldsOnly();
      fetchCarts();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to add item';
      toast.error(typeof msg === 'string' ? msg : 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditItem = (userId, item) => {
    setEditingItem(`${userId}-${item.item_index}`);
    setEditDraft({ price: String(item.price ?? ''), quantity: item.quantity || 1 });
  };

  const cancelEditItem = () => {
    setEditingItem(null);
    setEditDraft({ price: '', quantity: 1 });
  };

  const saveEditItem = async (userId, itemIndex) => {
    const priceNum = parseFloat(editDraft.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Enter a valid price');
      return;
    }
    const qtyNum = parseInt(editDraft.quantity, 10);
    if (isNaN(qtyNum) || qtyNum < 1) {
      toast.error('Enter a valid quantity');
      return;
    }
    const key = `${userId}-${itemIndex}`;
    try {
      setItemBusy(key);
      await axios.post(
        `${API}/admin/carts/update-item`,
        { user_id: userId, item_index: itemIndex, price: priceNum, quantity: qtyNum },
        { headers: getAuthHeader() }
      );
      toast.success('Item updated');
      setEditingItem(null);
      fetchCarts();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to update item';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update item');
    } finally {
      setItemBusy(null);
    }
  };

  const removeItem = async (userId, itemIndex, productName) => {
    if (!window.confirm(`Remove "${productName}" from this cart?`)) return;
    const key = `${userId}-${itemIndex}`;
    try {
      setItemBusy(key);
      await axios.post(
        `${API}/admin/carts/remove-item`,
        { user_id: userId, item_index: itemIndex },
        { headers: getAuthHeader() }
      );
      toast.success('Item removed');
      fetchCarts();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to remove item';
      toast.error(typeof msg === 'string' ? msg : 'Failed to remove item');
    } finally {
      setItemBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-mono text-base text-white">USER CARTS</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={openAddModal}
              variant="ghost"
              size="sm"
              className="text-tiffany-500 hover:text-white hover:bg-tiffany-500/10 rounded-none h-8 px-2 font-mono text-xs"
              data-testid="admin-carts-add-custom-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              ADD ITEM
            </Button>
            <Button
              onClick={fetchCarts}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white rounded-none h-8 w-8 p-0"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-14 pb-8 px-3 max-w-6xl mx-auto">
        {/* Summary - Compact */}
        <div className="flex items-center gap-4 py-3 border-b border-zinc-800 mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-orange-500" />
            <span className="font-mono text-sm text-white">{summary.total_carts}</span>
            <span className="font-mono text-xs text-zinc-500">carts</span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-blue-500" />
            <span className="font-mono text-sm text-white">{summary.total_items}</span>
            <span className="font-mono text-xs text-zinc-500">items</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span className="font-mono text-sm text-white">{formatCurrency(summary.total_value)}</span>
          </div>
        </div>

        {/* Carts List - Compact */}
        {carts.length === 0 ? (
          <div className="border border-zinc-800 p-6 text-center">
            <ShoppingCart className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="font-mono text-sm text-zinc-500">NO ACTIVE CARTS</p>
          </div>
        ) : (
          <div className="space-y-2">
            {carts.map((cart, idx) => {
              const isExpanded = expandedCarts[cart.user_id];
              
              return (
                <div 
                  key={cart.user_id || idx}
                  className="border border-zinc-800 bg-zinc-900/30"
                >
                  {/* Collapsed View - Always Visible */}
                  <button
                    onClick={() => toggleCart(cart.user_id)}
                    className="w-full p-3 flex items-center gap-3 text-left"
                  >
                    <User className="w-4 h-4 text-tiffany-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <p className="font-mono text-sm text-white truncate">{cart.user_name || cart.user_email}</p>
                      {cart.user_has_blkcrd && (
                        <Badge
                          className="bg-purple-500/20 text-purple-400 border-0 rounded-none text-[9px] tracking-wider flex-shrink-0"
                          data-testid={`blkcrd-badge-${cart.user_id}`}
                        >
                          BLKCRD
                        </Badge>
                      )}
                    </div>
                    <Badge className="bg-orange-500/20 text-orange-400 border-0 rounded-none text-[10px] flex-shrink-0">
                      {cart.item_count}
                    </Badge>
                    {cart.user_has_blkcrd && cart.blkcrd_discount > 0 ? (
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="font-mono text-[10px] text-zinc-600 line-through">
                          {formatCurrency(cart.cart_total)}
                        </span>
                        <span
                          className="font-mono text-sm text-purple-400"
                          data-testid={`cart-total-${cart.user_id}`}
                        >
                          {formatCurrency(cart.cart_total_after_blkcrd)}
                        </span>
                      </span>
                    ) : (
                      <span
                        className="font-mono text-sm text-white flex-shrink-0"
                        data-testid={`cart-total-${cart.user_id}`}
                      >
                        {formatCurrency(cart.cart_total)}
                      </span>
                    )}
                    <span className="font-mono text-[10px] text-zinc-500 flex-shrink-0">{timeAgo(cart.updated_at)}</span>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    )}
                  </button>
                  
                  {/* Expanded View - Cart Items */}
                  {isExpanded && (
                    <div className="border-t border-zinc-800 p-3 space-y-2">
                      {cart.items.map((item, itemIdx) => {
                        const editKey = `${cart.user_id}-${item.item_index}`;
                        const isEditing = editingItem === editKey;
                        const isBusy = itemBusy === editKey;
                        return (
                        <div 
                          key={`${item.product_id}-${item.variant_id || itemIdx}-${item.item_index}`}
                          className="flex items-center gap-2"
                        >
                          {item.image_url ? (
                            <img 
                              src={item.image_url} 
                              alt={item.product_name}
                              className="w-8 h-8 object-cover bg-zinc-800 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-zinc-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-xs text-white truncate">{item.product_name}</p>
                            {item.variant_name && (
                              <p className="font-mono text-[10px] text-zinc-500">{item.variant_name}</p>
                            )}
                            {item.price_label && (
                              <p className={`font-mono text-[10px] ${
                                item.price_label === 'TRADE' ? 'text-orange-400' : 
                                item.price_label === 'OFFER' ? 'text-green-400' : 
                                'text-purple-400'
                              }`}>{item.price_label}</p>
                            )}
                          </div>

                          {isEditing ? (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="font-mono text-[10px] text-zinc-500">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editDraft.price}
                                onChange={(e) => setEditDraft(d => ({ ...d, price: e.target.value }))}
                                className="w-20 bg-zinc-900 border border-zinc-700 text-white font-mono text-xs rounded-none h-7 px-1"
                                data-testid={`edit-item-price-${item.item_index}`}
                              />
                              <span className="font-mono text-[10px] text-zinc-500">×</span>
                              <input
                                type="number"
                                min="1"
                                value={editDraft.quantity}
                                onChange={(e) => setEditDraft(d => ({ ...d, quantity: e.target.value }))}
                                className="w-12 bg-zinc-900 border border-zinc-700 text-white font-mono text-xs rounded-none h-7 px-1"
                                data-testid={`edit-item-qty-${item.item_index}`}
                              />
                              <button
                                onClick={() => saveEditItem(cart.user_id, item.item_index)}
                                disabled={isBusy}
                                className="h-7 w-7 flex items-center justify-center bg-tiffany-500 hover:bg-tiffany-600 text-black disabled:opacity-50"
                                data-testid={`save-item-${item.item_index}`}
                                title="Save"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={cancelEditItem}
                                disabled={isBusy}
                                className="h-7 w-7 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white disabled:opacity-50"
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-mono text-xs text-white flex-shrink-0">
                                {item.price_label || (item.original_price && item.price !== item.original_price) ? (
                                  <>
                                    <span className="text-green-400">{formatCurrency(item.price)}</span>
                                    {item.original_price && item.price !== item.original_price && (
                                      <span className="text-zinc-600 line-through ml-1 text-[10px]">{formatCurrency(item.original_price)}</span>
                                    )}
                                  </>
                                ) : (
                                  formatCurrency(item.price)
                                )} × {item.quantity}
                              </span>
                              <button
                                onClick={() => startEditItem(cart.user_id, item)}
                                disabled={isBusy}
                                className="h-7 w-7 flex items-center justify-center text-zinc-500 hover:text-tiffany-500 disabled:opacity-50"
                                data-testid={`edit-item-btn-${item.item_index}`}
                                title="Edit price/quantity"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => removeItem(cart.user_id, item.item_index, item.product_name)}
                                disabled={isBusy}
                                className="h-7 w-7 flex items-center justify-center text-zinc-500 hover:text-red-500 disabled:opacity-50"
                                data-testid={`remove-item-btn-${item.item_index}`}
                                title="Remove from cart"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        );
                      })}

                      {cart.user_has_blkcrd && cart.blkcrd_discount > 0 && (
                        <div className="border-t border-zinc-800 mt-2 pt-2 space-y-1" data-testid={`blkcrd-summary-${cart.user_id}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] text-zinc-500 tracking-wider">SUBTOTAL</span>
                            <span className="font-mono text-[11px] text-zinc-400">{formatCurrency(cart.cart_total)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] text-purple-400 tracking-wider">BLKCRD MEMBER (-15%)</span>
                            <span className="font-mono text-[11px] text-purple-500">-{formatCurrency(cart.blkcrd_discount)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] text-zinc-300 tracking-wider">ESTIMATED TOTAL</span>
                            <span className="font-mono text-sm text-purple-500 font-bold">{formatCurrency(cart.cart_total_after_blkcrd)}</span>
                          </div>
                        </div>
                      )}

                      <div className="border-t border-zinc-800 mt-3 pt-3 flex justify-end">
                        <button
                          onClick={() => openCheckoutModal(cart)}
                          className="px-3 py-1.5 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-[11px] font-bold tracking-wider"
                          data-testid={`checkout-for-${cart.user_id}`}
                        >
                          CHECKOUT FOR USER →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add Custom Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="add-custom-item-modal">
          <div className="bg-[#0a0a0a] border border-zinc-800 w-full max-w-md my-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-800 p-3">
              <h2 className="font-mono text-sm text-white flex items-center gap-2">
                ADD CUSTOM CART ITEM
                {itemsAddedCount > 0 && (
                  <span
                    className="bg-tiffany-500/20 border border-tiffany-500 text-tiffany-500 font-mono text-[10px] px-2 py-0.5"
                    data-testid="add-custom-item-added-count"
                  >
                    ✓ {itemsAddedCount} ADDED
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-zinc-500 hover:text-white"
                data-testid="add-custom-item-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* User search */}
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">USER (NAME OR EMAIL)</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <Input
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      if (selectedUser) {
                        setSelectedUser(null);
                        setAddForm(f => ({ ...f, user_email: '' }));
                      }
                    }}
                    placeholder="Search user by name or email..."
                    className="bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none h-9 pl-7"
                    data-testid="add-custom-item-user-search"
                  />
                </div>
                {searchingUsers && (
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">Searching...</p>
                )}
                {userResults.length > 0 && !selectedUser && (
                  <div className="border border-zinc-800 bg-zinc-900 mt-1 max-h-56 overflow-y-auto" data-testid="add-custom-item-user-results">
                    {userResults.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => selectUser(u)}
                        className="w-full flex items-center gap-2 p-2 hover:bg-zinc-800 text-left border-b border-zinc-800 last:border-b-0"
                      >
                        <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-zinc-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-white truncate">{u.full_name || '(no name)'}</p>
                          <p className="font-mono text-[10px] text-zinc-500 truncate">{u.email}</p>
                        </div>
                        {u.is_verified === false && (
                          <span className="font-mono text-[9px] text-orange-500 flex-shrink-0">UNVERIFIED</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {selectedUser && (
                  <p className="font-mono text-[10px] text-tiffany-500 mt-1 truncate">
                    ✓ {selectedUser.full_name ? `${selectedUser.full_name} — ` : ''}{selectedUser.email}
                  </p>
                )}
              </div>

              {/* Product search */}
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">PRODUCT</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <Input
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      if (selectedProduct) {
                        setSelectedProduct(null);
                        setAddForm(f => ({ ...f, product_id: '', product_name: '', variant_id: '' }));
                      }
                    }}
                    placeholder="Search product name..."
                    className="bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none h-9 pl-7"
                    data-testid="add-custom-item-product-search"
                  />
                </div>
                {searching && (
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">Searching...</p>
                )}
                {productResults.length > 0 && !selectedProduct && (
                  <div className="border border-zinc-800 bg-zinc-900 mt-1 max-h-56 overflow-y-auto" data-testid="add-custom-item-product-results">
                    {productResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => selectProduct(p)}
                        className="w-full flex items-center gap-2 p-2 hover:bg-zinc-800 text-left border-b border-zinc-800 last:border-b-0"
                      >
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-8 h-8 object-cover bg-zinc-800 flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 bg-zinc-800 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-white truncate">{p.name}</p>
                          <p className="font-mono text-[10px] text-zinc-500">${p.price}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedProduct && (
                  <p className="font-mono text-[10px] text-tiffany-500 mt-1">
                    ✓ {selectedProduct.name}
                  </p>
                )}
              </div>

              {/* Variant (if product has them) */}
              {selectedProduct?.has_variants && selectedProduct.variants?.length > 0 && (
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">VARIANT</label>
                  <select
                    value={addForm.variant_id}
                    onChange={(e) => setAddForm(f => ({ ...f, variant_id: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white font-mono text-sm rounded-none h-9 px-2"
                    data-testid="add-custom-item-variant"
                  >
                    <option value="">— Select variant —</option>
                    {selectedProduct.variants.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} {v.stock !== undefined ? `(stock: ${v.stock})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Type */}
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">TYPE</label>
                <div className="grid grid-cols-3 gap-1">
                  {['regular', 'trade', 'offer'].map(t => (
                    <button
                      key={t}
                      onClick={() => setAddForm(f => ({ ...f, type: t }))}
                      className={`font-mono text-[11px] h-8 border ${
                        addForm.type === t
                          ? 'border-tiffany-500 bg-tiffany-500/10 text-tiffany-500'
                          : 'border-zinc-800 text-zinc-500 hover:text-white'
                      }`}
                      data-testid={`add-custom-item-type-${t}`}
                    >
                      {t.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price + Quantity */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">PRICE ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addForm.price}
                    onChange={(e) => setAddForm(f => ({ ...f, price: e.target.value }))}
                    placeholder="0.00"
                    className="bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none h-9"
                    data-testid="add-custom-item-price"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">QTY</label>
                  <Input
                    type="number"
                    min="1"
                    value={addForm.quantity}
                    onChange={(e) => setAddForm(f => ({ ...f, quantity: e.target.value }))}
                    className="bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none h-9"
                    data-testid="add-custom-item-quantity"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setShowAddModal(false)}
                  variant="ghost"
                  className={`flex-1 border font-mono text-xs rounded-none h-9 ${
                    itemsAddedCount > 0
                      ? 'border-tiffany-500 text-tiffany-500 hover:text-tiffany-400'
                      : 'border-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="add-custom-item-done-btn"
                >
                  {itemsAddedCount > 0 ? 'DONE' : 'CANCEL'}
                </Button>
                <Button
                  onClick={submitAddItem}
                  disabled={submitting}
                  className="flex-1 bg-tiffany-500 hover:bg-tiffany-600 text-black font-mono text-xs rounded-none h-9"
                  data-testid="add-custom-item-submit"
                >
                  {submitting
                    ? 'ADDING...'
                    : itemsAddedCount > 0
                      ? 'ADD ANOTHER'
                      : 'ADD TO CART'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Checkout-for-User Modal */}
      {checkoutModal.open && checkoutModal.cart && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !checkoutBusy && setCheckoutModal({ open: false, cart: null })}>
          <div className="bg-zinc-900 border border-tiffany-500/50 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h2 className="font-mono text-base text-tiffany-500 tracking-wider">CHECKOUT FOR USER</h2>
                <p className="font-mono text-[11px] text-zinc-500 mt-0.5">{checkoutModal.cart.user_email} · {formatCurrency(checkoutModal.cart.cart_total_after_blkcrd ?? checkoutModal.cart.cart_total)}</p>
              </div>
              <button onClick={() => setCheckoutModal({ open: false, cart: null })} disabled={checkoutBusy} className="text-zinc-500 hover:text-white" data-testid="close-checkout-modal">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">SHIP TO NAME</label>
                <Input
                  value={checkoutForm.shipping_name}
                  onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_name: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  data-testid="checkout-ship-name"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">ADDRESS</label>
                <Input
                  value={checkoutForm.shipping_address}
                  onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_address: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  data-testid="checkout-ship-address"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">APT / UNIT (OPTIONAL)</label>
                <Input
                  value={checkoutForm.shipping_apt_unit}
                  onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_apt_unit: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  data-testid="checkout-ship-apt"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">CITY</label>
                  <Input
                    value={checkoutForm.shipping_city}
                    onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_city: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                    data-testid="checkout-ship-city"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">STATE</label>
                  <Input
                    value={checkoutForm.shipping_state}
                    onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_state: e.target.value.toUpperCase() }))}
                    maxLength={2}
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm uppercase"
                    data-testid="checkout-ship-state"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">ZIP</label>
                  <Input
                    value={checkoutForm.shipping_zip}
                    onChange={(e) => setCheckoutForm(f => ({ ...f, shipping_zip: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                    data-testid="checkout-ship-zip"
                  />
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.priority_shipping}
                    onChange={(e) => setCheckoutForm(f => ({
                      ...f,
                      priority_shipping: e.target.checked,
                      // Mutually exclusive with overnight
                      overnight_shipping: e.target.checked ? false : f.overnight_shipping,
                    }))}
                    className="accent-tiffany-500"
                    data-testid="checkout-priority-shipping"
                  />
                  <span className="font-mono text-xs text-white">PRIORITY SHIPPING <span className="text-zinc-500">(+$15)</span></span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.overnight_shipping}
                    onChange={(e) => setCheckoutForm(f => ({
                      ...f,
                      overnight_shipping: e.target.checked,
                      priority_shipping: e.target.checked ? false : f.priority_shipping,
                    }))}
                    className="accent-tiffany-500"
                    data-testid="checkout-overnight-shipping"
                  />
                  <span className="font-mono text-xs text-white">OVERNIGHT SHIPPING <span className="text-zinc-500">(+$50)</span></span>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-mono text-[10px] text-zinc-500 block mb-1">PROMO CODE</label>
                    <Input
                      value={checkoutForm.promo_code}
                      onChange={(e) => setCheckoutForm(f => ({ ...f, promo_code: e.target.value.toUpperCase() }))}
                      placeholder="OPTIONAL"
                      className="bg-zinc-800 border-zinc-700 font-mono text-sm uppercase"
                      data-testid="checkout-promo-code"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] text-zinc-500 block mb-1">GIFT CARD CODE</label>
                    <Input
                      value={checkoutForm.gift_card_code}
                      onChange={(e) => setCheckoutForm(f => ({ ...f, gift_card_code: e.target.value.toUpperCase() }))}
                      placeholder="OPTIONAL"
                      className="bg-zinc-800 border-zinc-700 font-mono text-sm uppercase"
                      data-testid="checkout-gift-card-code"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">MANUAL DISCOUNT ($ OFF)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={checkoutForm.manual_discount}
                    onChange={(e) => setCheckoutForm(f => ({ ...f, manual_discount: e.target.value }))}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                    data-testid="checkout-manual-discount"
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">ADMIN NOTES (OPTIONAL)</label>
                  <Input
                    value={checkoutForm.notes}
                    onChange={(e) => setCheckoutForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Internal note for this order"
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                    data-testid="checkout-notes"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setCheckoutModal({ open: false, cart: null })}
                disabled={checkoutBusy}
                className="font-mono text-xs"
                data-testid="checkout-cancel"
              >
                CANCEL
              </Button>
              <Button
                onClick={submitAdminCheckout}
                disabled={checkoutBusy}
                className="bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-xs font-bold tracking-wider"
                data-testid="checkout-submit"
              >
                {checkoutBusy ? 'PROCESSING…' : 'PLACE ORDER →'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
